import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import BookingBar from "@/components/BookingBar";
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, Tag } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1610475426033-e6411e62b767?crop=entropy&cs=srgb&fm=jpg&q=85";
const SPECIAL_IMG =
  "https://images.unsplash.com/photo-1562036861-44811e0b4481?crop=entropy&cs=srgb&fm=jpg&q=85";
const SPECIAL_VIDEO = "/videos/escape.mp4";

const BRAND_SLIDE = { type: "brand", id: "brand" };
const SPECIAL_SLIDE = {
  type: "special",
  id: "special-weekly",
  title: "The Weekly Escape Special",
  text: "10% off rentals of 7 days or more — plus a FREE baby seat on rentals of 12 days or more, for the little ones.",
  image: SPECIAL_IMG,
};

export default function HeroSlider() {
  const [slides, setSlides] = useState([BRAND_SLIDE, SPECIAL_SLIDE]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api
      .get("/vehicles")
      .then((r) => {
        const vs = r.data || [];
        const versa = vs.filter((v) => /versa/i.test(v.name || ""));
        const trax = vs.filter((v) => /trax/i.test(v.name || ""));
        const rest = vs.filter((v) => !/versa|trax/i.test(v.name || ""));
        const vehicleSlides = [...versa, ...trax, ...rest].slice(0, 4).map((v) => ({ type: "vehicle", ...v }));
        setSlides([BRAND_SLIDE, ...vehicleSlides, SPECIAL_SLIDE]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const dwell = slides[idx]?.type === "special" ? 13000 : 6000;
    const t = setTimeout(() => setIdx((i) => (i + 1) % slides.length), dwell);
    return () => clearTimeout(t);
  }, [slides.length, idx]);

  const go = (n) => setIdx((i) => (i + n + slides.length) % slides.length);

  return (
    <section data-testid="hero-slider" className="relative min-h-[92vh] overflow-hidden bg-black">
      {slides.map((s, i) => (
        <div
          key={s.id}
          data-testid={`hero-slide-${i}`}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100 z-10" : "opacity-0 z-0"}`}
        >
          {s.type === "special" ? (
            <video
              data-testid="hero-special-video"
              autoPlay
              muted
              loop
              playsInline
              poster={SPECIAL_IMG}
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="/videos/escape.webm" type="video/webm" />
              <source src={SPECIAL_VIDEO} type="video/mp4" />
            </video>
          ) : (
            <img
              src={s.type === "vehicle" ? s.image_url : HERO_IMG}
              alt={s.name || s.title || "Easy Drive Car Rental"}
              className="absolute inset-0 w-full h-full object-cover kenburns"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-black/40 to-black/20" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-40 pt-48 w-full min-h-[92vh] flex flex-col justify-end text-white">
            {s.type === "brand" && (
              <div key={idx === i ? "active" : "idle"} className={i === idx ? "fade-up" : ""}>
                <p className="inline-flex items-center gap-2 text-[11px] font-ui tracking-[0.3em] uppercase text-primary border border-primary/30 rounded-full px-4 py-2 bg-black/40 backdrop-blur-md">
                  <MapPin size={12} /> Nassau · The Bahamas
                </p>
                <h1 data-testid="home-hero-title" className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.08]" style={{ textShadow: "0 0 26px rgba(0,0,0,0.85), 0 0 70px rgba(199,30,46,0.4)" }}>
                  Your next road trip travel journey <span className="italic gold-text" style={{ textShadow: "0 0 30px rgba(230,36,52,0.75), 0 0 90px rgba(230,36,52,0.4)" }}>begins with us!</span>
                </h1>
                <p className="mt-5 text-sm sm:text-base text-white/85 max-w-xl leading-relaxed" style={{ textShadow: "0 1px 16px rgba(0,0,0,0.85)" }}>
                  An immaculately kept fleet of premium vehicles, delivered to your hotel or villa
                  gate, hotel or villa — anywhere on New Providence and Paradise Island.
                  Reserve in moments; pay by card, PayPal or Zelle.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/fleet">
                    <Button data-testid="home-browse-fleet-button" size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8 h-12">
                      Browse the Fleet <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                  <Link to="/check-in">
                    <Button data-testid="home-how-it-works-button" size="lg" variant="outline" className="rounded-full border-white/25 bg-black/30 backdrop-blur-md text-white hover:bg-black/50 hover:text-white font-ui px-8 h-12">
                      Express Check-In
                    </Button>
                  </Link>
                  <Link to="/get-quote">
                    <Button data-testid="home-get-quote-button" size="lg" variant="outline" className="rounded-full border-white/25 bg-black/30 backdrop-blur-md text-white hover:bg-black/50 hover:text-white font-ui px-8 h-12">
                      Get an Instant Quote
                    </Button>
                  </Link>
                </div>
              </div>
            )}
            {s.type === "vehicle" && (
              <div key={idx === i ? "active" : "idle"} className={i === idx ? "fade-up" : ""}>
                <p className="inline-flex items-center gap-2 text-[11px] font-ui tracking-[0.3em] uppercase text-primary border border-primary/30 rounded-full px-4 py-2 bg-black/40 backdrop-blur-md">
                  {s.category} · Airport drop-off available
                </p>
                <h2 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight max-w-3xl leading-[1.05]">
                  {s.name}
                </h2>
                <p className="mt-4 text-lg text-white/75">
                  From <span className="gold-text font-ui text-3xl font-semibold">{fmtMoney(s.price_per_day)}</span> per day
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to={`/book/${s.id}`}>
                    <Button data-testid={`hero-book-${s.id}`} size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8 h-12">
                      Reserve This Vehicle <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                  <Link to="/fleet">
                    <Button size="lg" variant="outline" className="rounded-full border-white/25 bg-black/30 backdrop-blur-md text-white hover:bg-black/50 hover:text-white font-ui px-8 h-12">
                      Full Fleet
                    </Button>
                  </Link>
                </div>
              </div>
            )}
            {s.type === "special" && (
              <div key={idx === i ? "active" : "idle"} className={i === idx ? "fade-up" : ""}>
                <p className="inline-flex items-center gap-2 text-[11px] font-ui tracking-[0.3em] uppercase text-primary border border-primary/30 rounded-full px-4 py-2 bg-black/40 backdrop-blur-md">
                  <Tag size={12} /> Limited-Time Offer
                </p>
                <h2 data-testid="hero-special-title" className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight max-w-3xl leading-[1.05]">
                  {s.title}
                </h2>
                <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl leading-relaxed">{s.text}</p>
                <div className="mt-10">
                  <Link to="/fleet">
                    <Button data-testid="hero-special-cta" size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8 h-12">
                      Claim the Special <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      <button
        data-testid="hero-slide-prev"
        onClick={() => go(-1)}
        aria-label="Previous slide"
        className="absolute left-4 top-[38%] -translate-y-1/2 z-30 w-11 h-11 rounded-full glass border flex items-center justify-center text-foreground hover:text-primary transition-colors duration-200"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        data-testid="hero-slide-next"
        onClick={() => go(1)}
        aria-label="Next slide"
        className="absolute right-4 top-[38%] -translate-y-1/2 z-30 w-11 h-11 rounded-full glass border flex items-center justify-center text-foreground hover:text-primary transition-colors duration-200"
      >
        <ChevronRight size={18} />
      </button>
      <div className="absolute right-6 top-[38%] -translate-y-1/2 z-20 hidden md:flex flex-col gap-2.5 items-end">
        {slides.map((s, i) => (
          <button
            key={s.id}
            data-testid={`hero-slide-dot-${i}`}
            onClick={() => setIdx(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`rounded-full transition-all duration-300 ${i === idx ? "w-2.5 h-8 bg-primary" : "w-2.5 h-2.5 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </section>
  );
}
